import React, { useEffect } from "react";
import { connect, ConnectedProps } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  $Button,
  $DateLabel,
  $Skeleton,
  $TableTree,
} from "us.common/components";
import * as Actions from "us.collection.debtor/actions";
import { handleFilter, handleSort } from "../../Functions";
import { IMergeHistory } from "./Interfaces";
import { RootState } from "us.helper/types";

/**
 * @description - Component to display merge debtor history.
 * @link Design Document - https://unicorn-solutions.atlassian.net/wiki/spaces/UnicornBOX/pages/3627384863/Handle+Duplicate+Debtors+UI+Implementation
 * @author Tharanga Niroshana <tharangan@unicorn-solutions.com>
 * @since 04/10/2024
 */

const MergeHistoryTable: React.FC<PropsFromRedux & IMergeHistory> = (props) => {
  const { t } = useTranslation();
  const { metaData, debtorHistory, getDebtorMergeHistory, onClose } = props;

  const { debtorId } = metaData.data ?? {};
  const { data, isLoading } = debtorHistory ?? {}

  useEffect(() => {
    if(debtorId){
      getDebtorMergeHistory && getDebtorMergeHistory({ debtorId });
    }    
  }, [metaData.data]);

  const columns: any = [
    {
      title: t("US.COLLECTION.COMMON:COMMON.NAME"),
      key: "name",
      dataIndex: "name",
      width: "200px",
      customSorter: (a: any, b: any) => a?.localeCompare(b),
      customFilter: true,
      customRenderParent: (text: any, record: any, index: number) => {
        return record.name;
      },
      customRenderChild: (text: any, record: any, index: number) => {
        return record.name;
      },
    },
    {
      title: t("US.COLLECTION.COMMON:COMMON.PERSON_NO"),
      dataIndex: "nin",
      key: "nin",
      width: "200px",
      customSorter: (a: any, b: any) => a - b,
      customFilter: true,
      customRenderParent: (text: any, record: any, index: number) => {
        return record.nin;
      },
      customRenderChild: (text: any, record: any, index: number) => {
        return record.nin;
      },
    },
    {
      title: t("US.COLLECTION.COMMON:COMMON.POSTAL_CODE"),
      dataIndex: "zipCode",
      key: "zipCode",
      width: "250px",
      customSorter: (a: any, b: any) => a - b,
      customFilter: true,
      customRenderParent: (text: any, record: any, index: number) => {
        return record.zipCode;
      },
      customRenderChild: (text: any, record: any, index: number) => {
        return record.zipCode;
      },
    },
    {
      title: t("US.COLLECTION.COMMON:COMMON.ADDRESS"),
      dataIndex: "address",
      key: "address",
      width: "250px",
      customSorter: (a: any, b: any) => a?.localeCompare(b),
      customFilter: true,
      customRenderParent: (text: any, record: any, index: number) => {
        return record.address;
      },
      customRenderChild: (text: any, record: any, index: number) => {
        return record.address;
      },
    },
    {
      title: t("US.COLLECTION.DEBTOR:DUPLICATE_DEBTORS.MERGED_DATE"),
      dataIndex: "mergeDate",
      key: "mergeDate",
      width: "250px",
      customSorter: (a: any, b: any) => a.localeCompare(b),
      customFilter: "date",
      customRenderParent: (text: any, record: any, index: number) => {
        return <$DateLabel value={record?.mergeDate} />;
      },
      customRenderChild: (text: any, record: any, index: number) => {
        return <$DateLabel value={record?.mergeDate} />;
      },
    },
    {
      title: t("US.COLLECTION.DEBTOR:DUPLICATE_DEBTORS.MERGED_USER"),
      dataIndex: "mergeUser",
      key: "mergeUser",
      width: "250px",
      customSorter: (a: any, b: any) => a?.localeCompare(b),
      customFilter: true,
      customRenderParent: (text: any, record: any, index: number) => {
        return record.mergeUser;
      },
      customRenderChild: (text: any, record: any, index: number) => {
        return record.mergeUser;
      },
    },
  ];

  return (
    <div>
      <$Skeleton
        loading={isLoading}
        active
        paragraph={{
          rows: 2,
        }}
      >
        <$TableTree
          rowKey={(record: any) => record.key}
          columns={columns}
          data={data}
          className="mt-2"
          onSort={handleSort}
          onFilter={handleFilter}
          filterOnType={true}
          firstColSkipFilterProps={-1}
          expandable={{
            defaultExpandAllRows: true,
          }}
          bordered
          resetOnSourceChange={true}
        />
        <div className="drawer-footer-fixed align-content-center justify-content-end">
          <$Button
            onClick={() => {
              onClose();
            }}
            size="small"
          >
            {t("US.COLLECTION.COMMON:COMMON.CANCEL")}
          </$Button>
        </div>
      </$Skeleton>
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  const { duplicateDebtors, domainView } = state;
  const { debtorHistory } = duplicateDebtors;
  const { metaData } = domainView;

  return {
    metaData,
    debtorHistory,
  };
};

const { debtorHistory } = Actions.duplicateDebtors;

const mapDispatchToProps = {
  getDebtorMergeHistory: debtorHistory.get,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(MergeHistoryTable);
